import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, NumberInput, Tag, TextArea, Toggle } from '@carbon/react';
import { formatVnd } from '@ruby-cms/shared';
import { api, ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';
import { capOf, type Budget } from './botBudget';
import { MoneyInput } from '../components/MoneyInput';

// Bots (P1-D09): he A reads these settings on every round of its live tables, he B on every
// betting wave of its mini games; a save applies without a restart. Bots bet virtual money only:
// no wallet, ledger, pool or KPI row. Bot money per round is capped (backend/lib/ngan_sach_bot.js):
// one amount for the hottest game, every other game by its hotness, or a game's own cap.
type Row = {
  id: string; he: 'A' | 'B'; ma: string; ten: string; bat: boolean; xepHang: boolean; soBot: number; soBotChat: number; overridden: boolean;
  cuoc: { bat: boolean; tyLeVaoVan: number; cuocMin: number; cuocMax: number; mucCuoc: number[]; mucDung: number[] };
  khoaHot: string; doHot: number; tienVan: number | null; tienVanRieng: number; soBotKhoang: [number, number] | null;
};
type Lists = { ds: string[]; nguon: 'cms' | 'file'; soFile: number; dung?: number };
type Data = {
  games: Row[]; names: Lists; chat: Lists; budget: Budget & { doHot: Record<string, number> };
  meta: { version: number; source: string; at: string | null }; pending: { id: string }[];
};
type Draft = { bat: boolean; soBot: number; soBotChat: number; xepHang: boolean; cuocBat: boolean; vaoVan: number; min: string; max: string; doHot: number; tienVan: string };
type BudgetDraft = { bat: boolean; top: string; doCong: number; mucTieu: number };

const pct = (x: number) => Math.round(x * 1000) / 10;
const toDraft = (g: Row): Draft => ({
  bat: g.bat, soBot: g.soBot, soBotChat: g.soBotChat, xepHang: g.xepHang, cuocBat: g.cuoc.bat, vaoVan: pct(g.cuoc.tyLeVaoVan),
  min: g.cuoc.cuocMin ? String(g.cuoc.cuocMin) : '', max: g.cuoc.cuocMax ? String(g.cuoc.cuocMax) : '',
  doHot: g.doHot, tienVan: g.tienVanRieng ? String(g.tienVanRieng) : '',
});
const toBudgetDraft = (b: Budget): BudgetDraft => ({ bat: b.bat, top: b.tienVanGameHotNhat ? String(b.tienVanGameHotNhat) : '', doCong: b.doCong, mucTieu: pct(b.tyLeMucTieu) });
const fromBudgetDraft = (d: BudgetDraft): Budget => ({ bat: d.bat, tienVanGameHotNhat: Number(d.top || 0), doCong: d.doCong, tyLeMucTieu: d.mucTieu / 100 });
const same = (a: Draft, b: Draft) => JSON.stringify(a) === JSON.stringify(b);
const num = (v: number | string) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };
const lines = (s: string) => s.split('\n').map((x) => x.trim()).filter(Boolean);
const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';

function toChange(g: Row, d: Draft) {
  return {
    bat: d.bat, soBotChat: d.soBotChat, xepHang: d.xepHang,
    // only a changed count: a he B range per wave (6-10) stays unless the operator sets a number
    ...(d.soBot !== g.soBot ? { soBot: d.soBot } : {}),
    cuoc: { bat: d.cuocBat, ...(g.he === 'A' ? { tyLeVaoVan: d.vaoVan / 100 } : {}), cuocMin: Number(d.min || 0), cuocMax: Number(d.max || 0) },
    // only what changed: an untouched row keeps following the server defaults
    ...(d.doHot !== g.doHot ? { doHot: d.doHot } : {}),
    ...(Number(d.tienVan || 0) !== g.tienVanRieng ? { tienVan: Number(d.tienVan || 0) } : {}),
  };
}
function describe(g: Row, d: Draft) {
  if (!d.bat) return 'Tắt bot';
  const range = d.min || d.max ? `cược ${d.min ? formatVnd(Number(d.min)) : '...'} đến ${d.max ? formatVnd(Number(d.max)) : '...'}` : 'cược theo mức của game';
  const cap = d.tienVan ? `tối đa ${formatVnd(Number(d.tienVan))} mỗi ván` : `độ hot ${d.doHot}`;
  const n = d.soBot === g.soBot && g.soBotKhoang ? `${g.soBotKhoang[0]}-${g.soBotKhoang[1]}` : String(d.soBot);
  return `${n} bot ${g.he === 'A' ? 'ngồi bàn' : 'mỗi đợt'}, ${d.soBotChat} bot chat, ${d.cuocBat ? range : 'không cược'}, ${cap}, ${d.xepHang ? 'có' : 'không'} trên bảng xếp hạng`;
}
function describeBudget(b: Budget) {
  if (!b.bat) return 'Không giới hạn tiền bot mỗi ván';
  return `Game hot nhất tối đa ${b.tienVanGameHotNhat ? formatVnd(b.tienVanGameHotNhat) : '...'} mỗi ván, độ chênh ${b.doCong}, bot đặt trung bình ${pct(b.tyLeMucTieu)}% trần`;
}

function ListEditor({ id, label, help, list, value, onChange, disabled }: {
  id: string; label: string; help: string; list: Lists; value: string | null; onChange: (v: string | null) => void; disabled: boolean;
}) {
  const shown = value ?? list.ds.join('\n');
  return (
    <section className="panel">
      <div className="panel__head">
        <h2>{label}</h2>
        {list.nguon === 'cms' ? <Tag size="sm" type="blue">CMS đặt, {list.ds.length} dòng</Tag> : <Tag size="sm" type="gray">file máy chủ, {list.soFile} dòng</Tag>}
      </div>
      <p className="muted">{help}</p>
      <TextArea id={id} labelText={label} hideLabel rows={8} value={shown} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
      {list.nguon === 'cms' && !disabled ? (
        <Button kind="ghost" size="sm" onClick={() => onChange('')}>Bỏ danh sách CMS, dùng lại file máy chủ</Button>
      ) : null}
    </section>
  );
}

export function BotsPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['bots'], queryFn: () => api.get<Data>('/api/admin/bots') });
  const [draft, setDraft] = useState<Map<string, Draft>>(new Map());
  const [names, setNames] = useState<string | null>(null);
  const [chat, setChat] = useState<string | null>(null);
  const [budget, setBudget] = useState<BudgetDraft | null>(null);
  const [base, setBase] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<Command | null>(null);
  const reset = () => { setDraft(new Map()); setNames(null); setChat(null); setBudget(null); setBase(null); };
  const budgetChanged = budget !== null && !!q.data && JSON.stringify(budget) !== JSON.stringify(toBudgetDraft(q.data.budget));
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/bots', {
      changes: Object.fromEntries([...draft].map(([id, d]) => [id, toChange(q.data!.games.find((g) => g.id === id)!, d)])),
      ...(names !== null ? { names: lines(names).length ? lines(names) : null } : {}),
      ...(chat !== null ? { chat: lines(chat).length ? lines(chat) : null } : {}),
      ...(budgetChanged ? { budget: fromBudgetDraft(budget!) } : {}),
      expect: base ?? q.data!.meta.version,
    }),
    onSuccess: (r) => { setSaving(false); reset(); setResult(r.command); qc.invalidateQueries({ queryKey: ['bots'] }); },
    onError: (e) => { if (isStale(e)) setSaving(false); },
  });
  const stale = isStale(m.error);

  if (q.isLoading) return <div className="page"><BlockSkeleton lines={10} /></div>;
  if (q.error || !q.data) return <div className="page"><ErrorState error={q.error} onRetry={() => q.refetch()} /></div>;
  const { games, meta, pending } = q.data;
  const editable = can('bots.config');
  const eff = (g: Row) => draft.get(g.id) ?? toDraft(g);
  const touch = () => setBase(base ?? meta.version);
  const edit = (g: Row, d: Draft) => {
    const n = new Map(draft);
    if (same(d, toDraft(g))) n.delete(g.id); else n.set(g.id, d);
    setDraft(n);
    touch();
  };
  const namesChanged = names !== null && lines(names).join('\n') !== q.data.names.ds.join('\n');
  const chatChanged = chat !== null && lines(chat).join('\n') !== q.data.chat.ds.join('\n');
  const count = draft.size + (namesChanged ? 1 : 0) + (chatChanged ? 1 : 0) + (budgetChanged ? 1 : 0);
  // caps as they will be after this draft: hotness edits change "the hottest game" for every row
  const bEff = budget ?? toBudgetDraft(q.data.budget);
  const bNow = fromBudgetDraft(bEff);
  const hotNow = { ...q.data.budget.doHot };
  for (const g of games) hotNow[g.khoaHot] = eff(g).doHot;
  // the server never caps below 20 bets of the smallest level (backend ngan_sach_bot SAN_MUC)
  const capNow = (g: Row) => {
    const c = capOf(bNow, eff(g).doHot, Number(eff(g).tienVan || 0), hotNow);
    return c === null ? null : Math.max(c, 20 * (g.cuoc.mucDung[0] ?? 0));
  };
  const invalid = [...draft].some(([, d]) => d.min && d.max && Number(d.min) > Number(d.max)) || (budgetChanged && bEff.bat && !Number(bEff.top));
  const dupNames = names !== null && new Set(lines(names)).size !== lines(names).length;

  return (
    <div className="page page--with-bar">
      <PageHeader title="Bot"
        lead="Bot tạo không khí ở bàn trực tiếp và mini game: tiền ảo, không có ví, không vào sổ giao dịch, quỹ, hoa hồng, vòng cược hay báo cáo. Máy chủ áp dụng từ ván hoặc đợt cược tiếp theo, không cần khởi động lại." />
      <div className="row health">
        <span>Cấu hình: {meta.source === 'default' ? <span className="muted">mặc định, chưa sửa từ CMS</span> : <>phiên bản <span className="num">{meta.version}</span>{meta.at ? <span className="muted">, <When v={meta.at} /></span> : null}</>}</span>
        {pending.length ? <span className="muted">{pending.length} lệnh đang gửi</span> : null}
        <Link className="cell-link" to="/games/control">Quỹ điều khiển bàn</Link>
      </div>
      {result ? (
        <InlineNotification kind={result.status === 'SUCCEEDED' ? 'success' : 'warning'} lowContrast onClose={() => setResult(null)}
          title={result.status === 'SUCCEEDED' ? 'Hệ A đã nhận cấu hình bot mới' : 'Lệnh chưa xong'}
          subtitle={result.status === 'SUCCEEDED' ? '' : (result.lastError && HE_A_ERRORS[result.lastError]) || result.lastError || result.status} />
      ) : null}
      {stale ? (
        <ActionableNotification kind="warning" inline lowContrast hideCloseButton title="Cấu hình bot vừa được người khác đổi"
          subtitle="Chưa lưu gì. Bỏ bản nháp và tải lại để xem cấu hình mới, rồi sửa lại."
          actionButtonLabel="Bỏ bản nháp và tải lại" onActionButtonClick={() => { reset(); m.reset(); qc.invalidateQueries({ queryKey: ['bots'] }); }} />
      ) : null}
      <section className={'panel' + (budgetChanged ? ' is-changed' : '')} aria-label="Tiền bot mỗi ván">
        <div className="panel__head">
          <h2>Tiền bot mỗi ván</h2>
          <Toggle id="ns-bat" size="sm" labelText="Giới hạn tiền bot mỗi ván" hideLabel labelA="Không giới hạn" labelB="Có giới hạn" toggled={bEff.bat} disabled={!editable}
            onToggle={(v) => { setBudget({ ...bEff, bat: v }); touch(); }} />
        </div>
        <p className="muted">Nhập một số cho game hot nhất; mỗi game khác tự tính theo độ hot của nó (cột Độ hot bên dưới). Bot tự giảm mức cược để tổng tiền bot một ván quanh mức trung bình và không bao giờ vượt mức tối đa. Tiền thắng lớn trên chữ chạy cũng theo mức này. Độ hot ở trang này chỉ dùng cho tiền bot, không đổi tỉ lệ nổ hũ.</p>
        <div className="row">
          <MoneyInput id="ns-top" labelText="Tối đa 1 ván, game hot nhất" className="money-input" placeholder="100.000.000" maxDigits={13}
            disabled={!editable || !bEff.bat} value={bEff.top || ''}
            invalid={budgetChanged && bEff.bat && !Number(bEff.top)} invalidText="Nhập số tiền"
            onChange={(v) => { setBudget({ ...bEff, top: v }); touch(); }} />
          <NumberInput id="ns-docong" label="Độ chênh game hot và game vắng" helperText="1: theo tỉ lệ độ hot; 2: game vắng ít tiền hơn hẳn" min={0} max={5} step={0.5} value={bEff.doCong}
            disabled={!editable || !bEff.bat} onChange={(_e, { value }) => { setBudget({ ...bEff, doCong: Math.min(5, Math.max(0, num(value))) }); touch(); }} />
          <NumberInput id="ns-muctieu" label="Bot đặt trung bình (% mức tối đa)" min={10} max={100} step={5} value={bEff.mucTieu}
            disabled={!editable || !bEff.bat} onChange={(_e, { value }) => { setBudget({ ...bEff, mucTieu: Math.min(100, Math.max(10, num(value))) }); touch(); }} />
        </div>
      </section>
      <table className="plain vis">
        <thead><tr><th>Game</th><th>Bot</th><th className="r">Số bot</th><th className="r">Bot chat</th><th>Cược</th><th className="r">Khoảng cược</th><th className="r">Tiền bot mỗi ván</th></tr></thead>
        <tbody>
          {games.map((g) => {
            const d = eff(g);
            return (
              <tr key={g.id} className={draft.has(g.id) ? 'is-changed' : undefined}>
                <td className="is-title">
                  <div className="vis__name">{g.ten}</div>
                  <div className="muted num">{g.ma} · hệ {g.he}{g.overridden ? <> · <Tag size="sm" type="blue">CMS đặt</Tag></> : null}</div>
                  <div className="muted">Mức bot đang cược: {d.cuocBat && g.cuoc.mucDung.length ? `${formatVnd(g.cuoc.mucDung[0]!)} - ${formatVnd(g.cuoc.mucDung[g.cuoc.mucDung.length - 1]!)} (${g.cuoc.mucDung.length} mức)` : '-'}</div>
                </td>
                <td data-label="Bot">
                  <Toggle id={'bot-' + g.id} size="sm" hideLabel labelText={'Bot ' + g.ten} labelA="Tắt" labelB="Bật" toggled={d.bat} disabled={!editable}
                    onToggle={(v) => edit(g, { ...d, bat: v })} />
                  <Toggle id={'xh-' + g.id} size="sm" hideLabel labelText="Bảng xếp hạng" labelA="Ẩn" labelB="Hiện" toggled={d.xepHang}
                    disabled={!editable} onToggle={(v) => edit(g, { ...d, xepHang: v })} />
                </td>
                <td className="r" data-label={g.he === 'A' ? 'Số bot ngồi bàn' : 'Số bot mỗi đợt'}>
                  <NumberInput id={'n-' + g.id} size="sm" hideLabel label={'Số bot ' + g.ten} min={0} max={g.he === 'A' ? 2000 : 200} step={1} value={d.soBot}
                    disabled={!editable || !d.bat} onChange={(_e, { value }) => edit(g, { ...d, soBot: Math.max(0, Math.trunc(num(value))) })} />
                  <div className="muted">{g.he === 'A' ? 'ngồi bàn' : d.soBot === g.soBot && g.soBotKhoang ? `${g.soBotKhoang[0]}-${g.soBotKhoang[1]} mỗi đợt` : 'mỗi đợt'}</div>
                </td>
                <td className="r" data-label="Bot chat">
                  <NumberInput id={'c-' + g.id} size="sm" hideLabel label={'Bot chat ' + g.ten} min={0} max={2000} step={1} value={d.soBotChat}
                    disabled={!editable || !d.bat} onChange={(_e, { value }) => edit(g, { ...d, soBotChat: Math.max(0, Math.trunc(num(value))) })} />
                </td>
                <td data-label="Cược">
                  <Toggle id={'cb-' + g.id} size="sm" hideLabel labelText={'Bot cược ' + g.ten} labelA="Không" labelB="Có" toggled={d.cuocBat}
                    disabled={!editable || !d.bat} onToggle={(v) => edit(g, { ...d, cuocBat: v })} />
                  {g.he === 'A' ? (
                    <NumberInput id={'vv-' + g.id} size="sm" label="% bot vào mỗi ván" min={0} max={100} step={5} value={d.vaoVan}
                      disabled={!editable || !d.bat || !d.cuocBat} onChange={(_e, { value }) => edit(g, { ...d, vaoVan: Math.min(100, Math.max(0, num(value))) })} />
                  ) : null}
                </td>
                <td className="r vis__money" data-label="Khoảng cược">
                  <MoneyInput id={'min-' + g.id} size="sm" labelText="Từ" className="money-input" placeholder="theo game" maxDigits={13}
                    disabled={!editable || !d.bat || !d.cuocBat} value={d.min || ''} onChange={(v) => edit(g, { ...d, min: v })} />
                  <MoneyInput id={'max-' + g.id} size="sm" labelText="Đến" className="money-input" placeholder="theo game" maxDigits={13}
                    disabled={!editable || !d.bat || !d.cuocBat} value={d.max || ''}
                    invalid={!!(d.min && d.max && Number(d.min) > Number(d.max))} invalidText="Nhỏ hơn mức từ"
                    onChange={(v) => edit(g, { ...d, max: v })} />
                </td>
                <td className="r vis__money" data-label="Tiền bot mỗi ván">
                  <NumberInput id={'hot-' + g.id} size="sm" label="Độ hot" min={0} max={100} step={0.1} value={d.doHot}
                    disabled={!editable} onChange={(_e, { value }) => edit(g, { ...d, doHot: Math.min(100, Math.max(0, Math.round(num(value) * 10) / 10)) })} />
                  <MoneyInput id={'tv-' + g.id} size="sm" labelText="Tối đa riêng" className="money-input" placeholder="theo độ hot" maxDigits={13}
                    disabled={!editable || !d.bat || !d.cuocBat} value={d.tienVan || ''} onChange={(v) => edit(g, { ...d, tienVan: v })} />
                  <div className="muted num" data-cap={g.id}>{(() => { const c = capNow(g); return c === null ? 'không giới hạn' : 'tối đa ' + formatVnd(c) + (d.tienVan ? ' (riêng)' : ''); })()}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="note">Bảng xếp hạng: tắt thì bot không được ghi thêm và các dòng bot cũ của game đó bị ẩn khỏi bảng; người chơi thật vẫn hiện như cũ.</p>
      <div className="split">
        <ListEditor id="bot-names" label="Tên bot" list={q.data.names} value={names} disabled={!editable}
          help="Mỗi dòng một tên, tối đa 24 ký tự, không trùng, không có dấu hai chấm. Để trống thì dùng danh sách trên máy chủ."
          onChange={(v) => { setNames(v); touch(); }} />
        <ListEditor id="bot-chat" label="Câu chat của bot" list={q.data.chat} value={chat} disabled={!editable}
          help="Mỗi dòng một câu, tối đa 120 ký tự. Câu có từ bị cấm bị bỏ qua. Để trống thì dùng danh sách trên máy chủ."
          onChange={(v) => { setChat(v); touch(); }} />
      </div>
      {dupNames ? <InlineNotification kind="error" lowContrast hideCloseButton title="Danh sách tên bot có tên trùng" /> : null}
      {count ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{count}</span> mục đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={reset}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={invalid || dupNames || stale} onClick={() => setSaving(true)}>Lưu</Button>
        </div>
      ) : null}
      <Modal open={saving} modalHeading={`Lưu ${count} thay đổi bot`} primaryButtonText="Gửi sang hệ A" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending} onRequestClose={() => setSaving(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          {draft.size ? (
            <table className="plain">
              <thead><tr><th>Game</th><th>Trước</th><th>Sau</th></tr></thead>
              <tbody>
                {[...draft].map(([id, d]) => {
                  const g = games.find((x) => x.id === id)!;
                  return <tr key={id}><td className="is-title">{g.ten}</td><td data-label="Trước">{describe(g, toDraft(g))}</td><td data-label="Sau">{describe(g, d)}</td></tr>;
                })}
              </tbody>
            </table>
          ) : null}
          {budgetChanged ? <p>Tiền bot mỗi ván: {describeBudget(fromBudgetDraft(budget!))}</p> : null}
          {namesChanged ? <p>Tên bot: {lines(names!).length ? `${lines(names!).length} tên từ CMS` : 'dùng lại file máy chủ'}</p> : null}
          {chatChanged ? <p>Câu chat: {lines(chat!).length ? `${lines(chat!).length} câu từ CMS` : 'dùng lại file máy chủ'}</p> : null}
          <InlineError error={stale ? null : m.error} />
        </div>
      </Modal>
    </div>
  );
}
